import { useMemo, useEffect } from 'react'
import {
  Box, Typography, Popover, Skeleton, alpha, useTheme, Divider,
} from '@mui/material'
import { Link, useLocation } from 'react-router-dom'
import { useCategories } from 'core'
import GridViewRoundedIcon      from '@mui/icons-material/GridViewRounded'
import StoreRoundedIcon         from '@mui/icons-material/StoreRounded'
import LocalOfferRoundedIcon    from '@mui/icons-material/LocalOfferRounded'
import ChevronRightRoundedIcon  from '@mui/icons-material/ChevronRightRounded'

// ─── Gradient fallbacks for categories without images ─────────────────────────
const GRADIENTS = [
  'linear-gradient(135deg,#6366F1,#8B5CF6)',
  'linear-gradient(135deg,#EC4899,#F97316)',
  'linear-gradient(135deg,#0EA5E9,#6366F1)',
  'linear-gradient(135deg,#10B981,#0EA5E9)',
  'linear-gradient(135deg,#F59E0B,#EF4444)',
  'linear-gradient(135deg,#8B5CF6,#EC4899)',
]

const buildTree = (categories) => {
  const map = {}
  const roots = []
  categories.forEach((c) => { map[c.id] = { ...c, children: [] } })
  categories.forEach((c) => {
    if (c.parent_id && map[c.parent_id]) map[c.parent_id].children.push(map[c.id])
    else roots.push(map[c.id])
  })
  return roots
}

// ─── Single parent category tile ─────────────────────────────────────────────
const CategoryTile = ({ cat, index, onClick }) => {
  const t = useTheme()
  const isDark = t.palette.mode === 'dark'
  const gradient = GRADIENTS[index % GRADIENTS.length]

  return (
    <Box
      component={Link}
      to={`/categories/${cat.slug}`}
      onClick={onClick}
      sx={{
        textDecoration: 'none',
        display: 'block',
        borderRadius: '14px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'transform 0.22s ease, box-shadow 0.22s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: isDark
            ? `0 8px 28px rgba(0,0,0,0.5), 0 0 0 1px ${alpha(t.palette.primary.main, 0.25)}`
            : `0 8px 24px rgba(0,0,0,0.12), 0 0 0 1px ${alpha(t.palette.primary.main, 0.15)}`,
        },
        '&:hover .cm-img': { transform: 'scale(1.07)' },
        '&:hover .cm-arrow': { opacity: 1, transform: 'translateX(0)' },
      }}
    >
      {/* Image area */}
      <Box sx={{
        position: 'relative', pt: '60%', overflow: 'hidden',
        bgcolor: isDark ? alpha(t.palette.background.paper, 0.5) : t.palette.grey[50],
      }}>
        {cat.image ? (
          <Box
            component="img"
            src={cat.image}
            alt={cat.name}
            className="cm-img"
            onError={(e) => { e.target.style.display = 'none' }}
            sx={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center',
              transition: 'transform 0.38s cubic-bezier(.25,.46,.45,.94)',
            }}
          />
        ) : (
          <Box className="cm-img" sx={{
            position: 'absolute', inset: 0,
            background: gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'transform 0.38s cubic-bezier(.25,.46,.45,.94)',
          }}>
            <Typography sx={{
              fontSize: '2rem', fontWeight: 900,
              color: alpha('#fff', 0.8),
              userSelect: 'none', textShadow: '0 2px 8px rgba(0,0,0,0.25)',
            }}>
              {cat.name?.charAt(0)?.toUpperCase()}
            </Typography>
          </Box>
        )}
        {/* Bottom fade */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 32,
          background: `linear-gradient(to top, ${t.palette.background.paper}, transparent)`,
          pointerEvents: 'none',
        }} />
      </Box>

      {/* Label row */}
      <Box sx={{
        px: 1.2, py: 1,
        display: 'flex', alignItems: 'center', gap: 0.5,
        bgcolor: 'background.paper',
      }}>
        <Typography noWrap sx={{
          fontSize: '0.75rem', fontWeight: 700,
          color: 'text.primary', flex: 1, lineHeight: 1.3,
        }}>
          {cat.name}
        </Typography>
        {cat.productCount != null && (
          <Typography sx={{
            fontSize: '0.6rem', fontWeight: 600,
            color: 'text.disabled', flexShrink: 0,
          }}>
            {cat.productCount}
          </Typography>
        )}
        <ChevronRightRoundedIcon
          className="cm-arrow"
          sx={{
            fontSize: 14, color: 'primary.main', flexShrink: 0,
            opacity: 0, transform: 'translateX(-4px)',
            transition: 'opacity 0.18s, transform 0.18s',
          }}
        />
      </Box>

      {/* Subcategory pills */}
      {cat.children?.length > 0 && (
        <Box sx={{
          px: 1.2, pb: 1,
          display: 'flex', flexWrap: 'wrap', gap: 0.5,
          bgcolor: 'background.paper',
        }}>
          {cat.children.slice(0, 3).map((child) => (
            <Box
              key={child.id}
              component={Link}
              to={`/categories/${child.slug}`}
              onClick={onClick}
              sx={{
                fontSize: '0.6rem', fontWeight: 600,
                color: 'text.secondary',
                bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                borderRadius: '6px', px: 0.7, py: 0.25,
                textDecoration: 'none',
                transition: 'background 0.15s, color 0.15s',
                '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
              }}
            >
              {child.name}
            </Box>
          ))}
          {cat.children.length > 3 && (
            <Typography sx={{
              fontSize: '0.6rem', fontWeight: 600, color: 'text.disabled',
              alignSelf: 'center',
            }}>
              +{cat.children.length - 3} more
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

// ─── Quick link item ──────────────────────────────────────────────────────────
const QuickLink = ({ icon, label, to, onClick, color }) => {
  const t = useTheme()
  return (
    <Box
      component={Link}
      to={to}
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.2,
        px: 1.5, py: 1.1,
        borderRadius: '10px',
        textDecoration: 'none',
        transition: 'background 0.15s',
        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.08) },
        '&:hover .ql-icon': { color: color || 'primary.main' },
        '&:hover .ql-arrow': { opacity: 1, transform: 'translateX(0)' },
      }}
    >
      <Box className="ql-icon" sx={{
        color: 'text.secondary',
        display: 'flex', alignItems: 'center',
        transition: 'color 0.15s',
      }}>
        {icon}
      </Box>
      <Typography sx={{
        fontSize: '0.8rem', fontWeight: 600,
        color: 'text.primary', flex: 1,
      }}>
        {label}
      </Typography>
      <ChevronRightRoundedIcon className="ql-arrow" sx={{
        fontSize: 15, color: 'text.disabled',
        opacity: 0, transform: 'translateX(-4px)',
        transition: 'opacity 0.15s, transform 0.15s',
      }} />
    </Box>
  )
}

// ─── Main MegaMenu ────────────────────────────────────────────────────────────
const CategoryMegaMenu = ({ anchorEl, open, onClose }) => {
  const { categories, isCategoriesLoading } = useCategories()
  const location = useLocation()
  const t = useTheme()
  const isDark = t.palette.mode === 'dark'
  const tree = useMemo(() => buildTree(categories), [categories])

  useEffect(() => {
    if (open) onClose()
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      disableScrollLock
      disableRestoreFocus
      ModalProps={{ keepMounted: false }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1.5,
            borderRadius: '20px',
            minWidth: 560,
            maxWidth: 780,
            p: 0,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
            boxShadow: isDark
              ? '0 24px 64px rgba(0,0,0,0.7)'
              : '0 16px 48px rgba(0,0,0,0.14)',
            bgcolor: isDark ? 'rgba(10,14,28,0.96)' : 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(20px)',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex' }}>

        {/* ── Left: Category tiles grid ─────────────────────── */}
        <Box sx={{ flex: 1, p: 2, minWidth: 0 }}>
          {/* Header */}
          <Typography sx={{
            fontSize: '0.65rem', fontWeight: 800,
            letterSpacing: 1.2, textTransform: 'uppercase',
            color: 'text.disabled', mb: 1.5, px: 0.5,
          }}>
            Shop by Category
          </Typography>

          {isCategoriesLoading ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular"
                  sx={{ borderRadius: '14px', height: 110 }} />
              ))}
            </Box>
          ) : tree.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
              No categories found.
            </Typography>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.5,
            }}>
              {tree.map((cat, i) => (
                <CategoryTile key={cat.id} cat={cat} index={i} onClick={onClose} />
              ))}
            </Box>
          )}
        </Box>

        {/* ── Right: Quick links sidebar ────────────────────── */}
        <Box sx={{
          width: 180,
          flexShrink: 0,
          borderLeft: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          p: 1.5,
          gap: 0.5,
        }}>
          <Typography sx={{
            fontSize: '0.65rem', fontWeight: 800,
            letterSpacing: 1.2, textTransform: 'uppercase',
            color: 'text.disabled', mb: 0.5, px: 1.5,
          }}>
            Quick Links
          </Typography>

          <QuickLink
            icon={<GridViewRoundedIcon sx={{ fontSize: 17 }} />}
            label="All Categories"
            to="/categories"
            onClick={onClose}
          />
          <QuickLink
            icon={<StoreRoundedIcon sx={{ fontSize: 17 }} />}
            label="Shop by Brand"
            to="/brands"
            onClick={onClose}
          />
          <QuickLink
            icon={<LocalOfferRoundedIcon sx={{ fontSize: 17 }} />}
            label="Deals & Offers"
            to="/offers"
            onClick={onClose}
          />

          {/* Promo banner */}
          <Box sx={{ mt: 'auto', pt: 1.5 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Box
              component={Link}
              to="/offers"
              onClick={onClose}
              sx={{
                display: 'block',
                borderRadius: '12px',
                overflow: 'hidden',
                textDecoration: 'none',
                background: 'linear-gradient(135deg, #6366F1 0%, #EC4899 100%)',
                p: 1.5,
                transition: 'opacity 0.18s',
                '&:hover': { opacity: 0.9 },
              }}
            >
              <Typography sx={{
                fontSize: '0.65rem', fontWeight: 800,
                color: alpha('#fff', 0.75), letterSpacing: 0.8,
                textTransform: 'uppercase', mb: 0.3,
              }}>
                Limited time
              </Typography>
              <Typography sx={{
                fontSize: '0.82rem', fontWeight: 800,
                color: '#fff', lineHeight: 1.3,
              }}>
                Flash Deals — Up to 50% off
              </Typography>
            </Box>
          </Box>
        </Box>

      </Box>
    </Popover>
  )
}

export default CategoryMegaMenu
